import { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay,
  parseISO,
} from "date-fns";
import { es } from "date-fns/locale";
import {
  CaretLeft,
  CaretRight,
  Plus,
  X,
  InstagramLogo,
  FacebookLogo,
  TiktokLogo,
  LinkedinLogo,
  XLogo,
  Clock,
  Trash,
  PencilSimple,
  Image,
  CalendarBlank,
} from "@phosphor-icons/react";
import axios from "axios";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CalendarSection = () => {
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [posts, setPosts] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [newPost, setNewPost] = useState({
    content: "",
    platforms: [],
    scheduled_date: "",
    scheduled_time: "12:00",
    status: "draft",
    image_url: "",
  });

  useEffect(() => {
    fetchPosts();
  }, [currentMonth]);

  const fetchPosts = async () => {
    try {
      const response = await axios.get(`${API}/platform/calendar/posts`, {
        withCredentials: true,
      });
      setPosts(response.data);
    } catch (error) {
      // Use demo data if API fails
      setPosts([
        {
          id: "1",
          content: "¡Nueva colección de verano! 🌴☀️",
          platforms: ["instagram", "facebook"],
          scheduled_date: format(addDays(new Date(), 2), "yyyy-MM-dd"),
          scheduled_time: "10:00",
          status: "scheduled",
        },
        {
          id: "2",
          content: "Detrás de cámaras de nuestro último proyecto",
          platforms: ["tiktok", "instagram"],
          scheduled_date: format(addDays(new Date(), 5), "yyyy-MM-dd"),
          scheduled_time: "18:00",
          status: "draft",
        },
        {
          id: "3",
          content: "Tips para mejorar tu productividad 💡",
          platforms: ["linkedin"],
          scheduled_date: format(addDays(new Date(), 7), "yyyy-MM-dd"),
          scheduled_time: "09:00",
          status: "scheduled",
        },
      ]);
    }
  };

  const savePost = async () => {
    if (!newPost.content || newPost.platforms.length === 0 || !newPost.scheduled_date) {
      toast.error("Completa todos los campos requeridos");
      return;
    }

    try {
      if (editingPost) {
        await axios.patch(
          `${API}/platform/calendar/posts/${editingPost.id}`,
          newPost,
          { withCredentials: true }
        );
        toast.success("Publicación actualizada");
      } else {
        await axios.post(`${API}/platform/calendar/posts`, newPost, {
          withCredentials: true,
        });
        toast.success("Publicación programada");
      }
      fetchPosts();
      closeModal();
    } catch (error) {
      // Demo mode - add locally
      const post = {
        id: Date.now().toString(),
        ...newPost,
      };
      setPosts([...posts, post]);
      toast.success("Publicación programada (demo)");
      closeModal();
    }
  };

  const deletePost = async (postId) => {
    try {
      await axios.delete(`${API}/platform/calendar/posts/${postId}`, {
        withCredentials: true,
      });
      toast.success("Publicación eliminada");
    } catch (error) {
      // Demo mode
    }
    setPosts(posts.filter((p) => p.id !== postId));
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingPost(null);
    setNewPost({
      content: "",
      platforms: [],
      scheduled_date: "",
      scheduled_time: "12:00",
      status: "draft",
      image_url: "",
    });
  };

  const openEditModal = (post) => {
    setEditingPost(post);
    setNewPost(post);
    setShowModal(true);
  };

  const openNewModal = (date) => {
    setNewPost({
      ...newPost,
      scheduled_date: format(date, "yyyy-MM-dd"),
    });
    setShowModal(true);
  };

  const togglePlatform = (platform) => {
    if (newPost.platforms.includes(platform)) {
      setNewPost({
        ...newPost,
        platforms: newPost.platforms.filter((p) => p !== platform),
      });
    } else {
      setNewPost({
        ...newPost,
        platforms: [...newPost.platforms, platform],
      });
    }
  };

  const platforms = [
    { id: "instagram", name: "Instagram", icon: InstagramLogo, color: "bg-gradient-to-r from-purple-500 to-pink-500" },
    { id: "facebook", name: "Facebook", icon: FacebookLogo, color: "bg-blue-600" },
    { id: "tiktok", name: "TikTok", icon: TiktokLogo, color: "bg-black" },
    { id: "linkedin", name: "LinkedIn", icon: LinkedinLogo, color: "bg-blue-700" },
    { id: "x", name: "X", icon: XLogo, color: "bg-zinc-900" },
  ];

  const getPlatformIcon = (platformId) => {
    const platform = platforms.find((p) => p.id === platformId);
    if (!platform) return null;
    const Icon = platform.icon;
    return <Icon size={14} weight="fill" />;
  };

  // Calendar rendering
  const renderHeader = () => (
    <div className="flex items-center justify-between mb-6">
      <h2 className="text-xl font-medium text-white">
        {format(currentMonth, "MMMM yyyy", { locale: es })}
      </h2>
      <div className="flex items-center gap-2">
        <button
          onClick={() => setCurrentMonth(subMonths(currentMonth, 1))}
          className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
        >
          <CaretLeft size={20} className="text-zinc-400" />
        </button>
        <button
          onClick={() => setCurrentMonth(new Date())}
          className="px-3 py-1 text-sm text-zinc-400 hover:text-white hover:bg-zinc-800 rounded-lg transition-colors"
        >
          Hoy
        </button>
        <button
          onClick={() => setCurrentMonth(addMonths(currentMonth, 1))}
          className="p-2 hover:bg-zinc-800 rounded-lg transition-colors"
        >
          <CaretRight size={20} className="text-zinc-400" />
        </button>
      </div>
    </div>
  );

  const renderDays = () => {
    const days = ["Dom", "Lun", "Mar", "Mié", "Jue", "Vie", "Sáb"];
    return (
      <div className="grid grid-cols-7 mb-2">
        {days.map((day) => (
          <div key={day} className="text-center text-xs text-zinc-500 font-medium py-2">
            {day}
          </div>
        ))}
      </div>
    );
  };

  const renderCells = () => {
    const monthStart = startOfMonth(currentMonth);
    const monthEnd = endOfMonth(monthStart);
    const startDate = startOfWeek(monthStart);
    const endDate = endOfWeek(monthEnd);

    const rows = [];
    let days = [];
    let day = startDate;

    while (day <= endDate) {
      for (let i = 0; i < 7; i++) {
        const dayPosts = posts.filter(
          (post) => post.scheduled_date === format(day, "yyyy-MM-dd")
        );
        const isCurrentMonth = isSameMonth(day, monthStart);
        const isToday = isSameDay(day, new Date());
        const isSelected = isSameDay(day, selectedDate);
        const dayClone = day;

        days.push(
          <div
            key={day.toString()}
            onClick={() => {
              setSelectedDate(dayClone);
              if (isCurrentMonth) openNewModal(dayClone);
            }}
            className={`min-h-[100px] p-2 border border-zinc-800 cursor-pointer transition-colors ${
              !isCurrentMonth ? "bg-zinc-900/50 text-zinc-600" : "hover:bg-zinc-800/50"
            } ${isToday ? "border-blue-500" : ""} ${isSelected ? "bg-zinc-800" : ""}`}
          >
            <div className={`text-sm mb-1 ${isToday ? "text-blue-400 font-bold" : "text-zinc-400"}`}>
              {format(day, "d")}
            </div>
            <div className="space-y-1">
              {dayPosts.slice(0, 2).map((post) => (
                <div
                  key={post.id}
                  onClick={(e) => {
                    e.stopPropagation();
                    openEditModal(post);
                  }}
                  className={`text-xs p-1 rounded truncate ${
                    post.status === "scheduled"
                      ? "bg-emerald-500/20 text-emerald-400"
                      : "bg-zinc-700/50 text-zinc-400"
                  }`}
                >
                  <div className="flex items-center gap-1">
                    {post.platforms.slice(0, 2).map((p) => (
                      <span key={p}>{getPlatformIcon(p)}</span>
                    ))}
                    <span className="truncate">{post.content.slice(0, 15)}...</span>
                  </div>
                </div>
              ))}
              {dayPosts.length > 2 && (
                <div className="text-xs text-zinc-500">+{dayPosts.length - 2} más</div>
              )}
            </div>
          </div>
        );
        day = addDays(day, 1);
      }
      rows.push(
        <div key={day.toString()} className="grid grid-cols-7">
          {days}
        </div>
      );
      days = [];
    }
    return rows;
  };

  return (
    <div className="space-y-6" data-testid="calendar-section">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-medium text-white">Calendario Editorial</h1>
          <p className="text-zinc-500 text-sm">Planifica y programa tu contenido</p>
        </div>
        <button
          onClick={() => openNewModal(new Date())}
          className="px-4 py-2 bg-white text-black rounded-lg font-medium text-sm flex items-center gap-2 hover:bg-zinc-200 transition-colors"
          data-testid="new-post-button"
        >
          <Plus size={18} weight="bold" />
          Nueva Publicación
        </button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-[#13151A] border border-zinc-800 rounded-lg p-4">
          <div className="text-zinc-500 text-xs uppercase tracking-widest mb-2">Este Mes</div>
          <div className="text-3xl font-mono text-white">{posts.length}</div>
          <div className="text-zinc-500 text-xs">publicaciones</div>
        </div>
        <div className="bg-[#13151A] border border-zinc-800 rounded-lg p-4">
          <div className="text-zinc-500 text-xs uppercase tracking-widest mb-2">Programadas</div>
          <div className="text-3xl font-mono text-emerald-400">
            {posts.filter((p) => p.status === "scheduled").length}
          </div>
        </div>
        <div className="bg-[#13151A] border border-zinc-800 rounded-lg p-4">
          <div className="text-zinc-500 text-xs uppercase tracking-widest mb-2">Borradores</div>
          <div className="text-3xl font-mono text-yellow-400">
            {posts.filter((p) => p.status === "draft").length}
          </div>
        </div>
        <div className="bg-[#13151A] border border-zinc-800 rounded-lg p-4">
          <div className="text-zinc-500 text-xs uppercase tracking-widest mb-2">Publicadas</div>
          <div className="text-3xl font-mono text-blue-400">
            {posts.filter((p) => p.status === "published").length}
          </div>
        </div>
      </div>

      {/* Calendar */}
      <div className="bg-[#13151A] border border-zinc-800 rounded-lg p-6">
        {renderHeader()}
        {renderDays()}
        {renderCells()}
      </div>

      {/* Post Modal */}
      <AnimatePresence>
        {showModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4"
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.95, opacity: 0 }}
              className="bg-[#13151A] border border-zinc-800 rounded-xl w-full max-w-lg max-h-[90vh] overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="p-6 border-b border-zinc-800 flex items-center justify-between">
                <h2 className="text-xl font-medium text-white">
                  {editingPost ? "Editar Publicación" : "Nueva Publicación"}
                </h2>
                <button onClick={closeModal} className="text-zinc-500 hover:text-white">
                  <X size={24} />
                </button>
              </div>

              <div className="p-6 space-y-6">
                {/* Platforms */}
                <div>
                  <label className="text-sm text-zinc-400 mb-3 block">Plataformas *</label>
                  <div className="flex flex-wrap gap-2">
                    {platforms.map((platform) => {
                      const Icon = platform.icon;
                      const isSelected = newPost.platforms.includes(platform.id);
                      return (
                        <button
                          key={platform.id}
                          onClick={() => togglePlatform(platform.id)}
                          className={`flex items-center gap-2 px-4 py-2 rounded-lg border transition-all ${
                            isSelected
                              ? "bg-white text-black border-white"
                              : "border-zinc-700 text-zinc-400 hover:border-zinc-500"
                          }`}
                          data-testid={`platform-${platform.id}`}
                        >
                          <Icon size={18} weight={isSelected ? "fill" : "regular"} />
                          <span className="text-sm">{platform.name}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>

                {/* Content */}
                <div>
                  <label className="text-sm text-zinc-400 mb-2 block">Contenido *</label>
                  <textarea
                    value={newPost.content}
                    onChange={(e) => setNewPost({ ...newPost, content: e.target.value })}
                    placeholder="Escribe el contenido de tu publicación..."
                    rows={4}
                    className="w-full px-4 py-3 bg-[#0A0A0A] border border-zinc-800 rounded-lg text-white placeholder:text-zinc-600 focus:outline-none focus:ring-1 focus:ring-white resize-none"
                    data-testid="post-content-input"
                  />
                  <div className="text-right text-xs text-zinc-500 mt-1">
                    {newPost.content.length} caracteres
                  </div>
                </div>

                {/* Date & Time */}
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm text-zinc-400 mb-2 block">Fecha *</label>
                    <input
                      type="date"
                      value={newPost.scheduled_date}
                      onChange={(e) => setNewPost({ ...newPost, scheduled_date: e.target.value })}
                      className="w-full px-4 py-3 bg-[#0A0A0A] border border-zinc-800 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-white"
                      data-testid="post-date-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm text-zinc-400 mb-2 block">Hora</label>
                    <input
                      type="time"
                      value={newPost.scheduled_time}
                      onChange={(e) => setNewPost({ ...newPost, scheduled_time: e.target.value })}
                      className="w-full px-4 py-3 bg-[#0A0A0A] border border-zinc-800 rounded-lg text-white focus:outline-none focus:ring-1 focus:ring-white"
                      data-testid="post-time-input"
                    />
                  </div>
                </div>

                {/* Status */}
                <div>
                  <label className="text-sm text-zinc-400 mb-2 block">Estado</label>
                  <div className="flex gap-2">
                    <button
                      onClick={() => setNewPost({ ...newPost, status: "draft" })}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                        newPost.status === "draft"
                          ? "bg-yellow-500/20 text-yellow-400 border border-yellow-500/30"
                          : "bg-zinc-800 text-zinc-400"
                      }`}
                    >
                      Borrador
                    </button>
                    <button
                      onClick={() => setNewPost({ ...newPost, status: "scheduled" })}
                      className={`flex-1 py-2 rounded-lg text-sm font-medium transition-colors ${
                        newPost.status === "scheduled"
                          ? "bg-emerald-500/20 text-emerald-400 border border-emerald-500/30"
                          : "bg-zinc-800 text-zinc-400"
                      }`}
                    >
                      Programar
                    </button>
                  </div>
                </div>

                {/* Actions */}
                <div className="flex gap-3 pt-4">
                  {editingPost && (
                    <button
                      onClick={() => {
                        deletePost(editingPost.id);
                        closeModal();
                      }}
                      className="px-4 py-3 border border-red-500/30 text-red-400 rounded-lg hover:bg-red-500/10 transition-colors"
                    >
                      <Trash size={20} />
                    </button>
                  )}
                  <button
                    onClick={closeModal}
                    className="flex-1 py-3 border border-zinc-800 text-zinc-400 rounded-lg hover:bg-zinc-800 transition-colors"
                  >
                    Cancelar
                  </button>
                  <button
                    onClick={savePost}
                    className="flex-1 py-3 bg-white text-black rounded-lg font-medium hover:bg-zinc-200 transition-colors"
                    data-testid="save-post-button"
                  >
                    {editingPost ? "Guardar" : "Programar"}
                  </button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default CalendarSection;
